import { useCallback, useEffect, useRef, useState } from 'react';
import type { Color, Look, MatchServerMessage, RatedClock } from '../../shared/protocol.ts';
import { saveSeatToken } from './api.ts';

export type MatchState =
  | { phase: 'idle' }
  | { phase: 'searching'; clock: RatedClock; since: number }
  | { phase: 'matched'; code: string; color: Color; opponent: { name: string; rating: number | null; look: Look | null } }
  | { phase: 'error'; reason: 'network' | 'replaced' };

const CLOSE_REPLACED = 4001;
const HEARTBEAT_MS = 20_000;

/**
 * One search for an opponent over a WebSocket to the matchmaker. Signed-in
 * players are matched by rating; guests send their nickname and meet guests.
 */
export function useMatchmaking() {
  const [state, setState] = useState<MatchState>({ phase: 'idle' });
  const socketRef = useRef<WebSocket | null>(null);

  const stop = useCallback(() => {
    const ws = socketRef.current;
    socketRef.current = null;
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, 'cancelled');
  }, []);

  const start = useCallback(
    (clock: RatedClock, guest: { name: string; locale: string }) => {
      stop();
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const query = new URLSearchParams({ clock, name: guest.name, locale: guest.locale });
      const ws = new WebSocket(`${protocol}://${window.location.host}/api/match/ws?${query}`);
      socketRef.current = ws;
      setState({ phase: 'searching', clock, since: Date.now() });
      let matched = false;
      const heartbeat = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, HEARTBEAT_MS);

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        let msg: MatchServerMessage;
        try {
          msg = JSON.parse(event.data as string) as MatchServerMessage;
        } catch {
          return;
        }
        if (msg.type === 'matched') {
          matched = true;
          saveSeatToken(msg.code, msg.token);
          setState({ phase: 'matched', code: msg.code, color: msg.color, opponent: msg.opponent });
        }
      };
      ws.onclose = (event) => {
        window.clearInterval(heartbeat);
        if (matched || socketRef.current !== ws) return; // finished or cancelled
        socketRef.current = null;
        setState({ phase: 'error', reason: event.code === CLOSE_REPLACED ? 'replaced' : 'network' });
      };
    },
    [stop],
  );

  const cancel = useCallback(() => {
    stop();
    setState({ phase: 'idle' });
  }, [stop]);

  // Leaving the page stops the search.
  useEffect(() => stop, [stop]);

  return { state, start, cancel };
}
